import type { ReactNode } from 'react';
import { Icon, type IconName } from './Icon';
import './SidebarRail.css';

export interface SidebarRailItem {
  id: string;
  icon: IconName;
  label: string;
}

/** DESIGN.md §9.2 `SidebarRail` (56px icon rail — UI-RESEARCH.md round 1 surface 1: "left rail =
 * navigation only"). The app's top-level nav between Library / Cockpit / Settings — the first
 * real router-equivalent App.tsx has needed, so it lives here rather than in any one feature. */
export function SidebarRail({
  items,
  activeId,
  onSelect,
  footer,
  navLabel,
}: {
  items: SidebarRailItem[];
  activeId: string;
  onSelect: (id: string) => void;
  footer?: ReactNode;
  /** The `<nav>` landmark's accessible name. `ui/` stays dictionary-free (see `SearchField.tsx`'s
   * rule), so `App.tsx` — this component's one caller — passes `t('common.mainNavigation')`.
   *
   * MVP-357: required. It was optional with an English `?? 'Main navigation'` fallback, defended by
   * MVP-289 as cover for surfaces not yet enrolled in the dictionary; Phase 7 enrolled the last of
   * them, so the fallback covered nothing and only kept an English landmark name alive. */
  navLabel: string;
}) {
  return (
    <nav className="sidebar-rail" aria-label={navLabel}>
      <ul className="sidebar-rail__list">
        {items.map((item) => {
          const active = item.id === activeId;
          return (
            <li key={item.id}>
              <button
                type="button"
                className={`sidebar-rail__item${active ? ' sidebar-rail__item--active' : ''}`}
                aria-current={active ? 'page' : undefined}
                title={item.label}
                onClick={() => onSelect(item.id)}
              >
                <Icon name={item.icon} size={20} />
                <span className="visually-hidden">{item.label}</span>
              </button>
            </li>
          );
        })}
      </ul>
      {footer && <div className="sidebar-rail__footer">{footer}</div>}
    </nav>
  );
}
